"use client"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import type { StepProps } from "@/types/listing"
import { useState, useEffect, useCallback, useRef } from "react"

import { ShippingData, ListingData } from "@/types/listing"
import { motion } from "framer-motion"
import { ChevronRight } from "lucide-react"
import { creationFlowBlockClass, creationFlowSectionLabelClass } from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"

const CONDITION_KEYS = ["new", "preOwned", "custom"] as const
const CONDITION_VALUES = ["new", "pre-owned", "custom"] as const
const GRADE_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"] as const

export function ConditionStep({ data = { title: '', description: '', photos: [], condition: '', price: 0, grade: '', shipping: {} as ShippingData, quantity: 1 }, updateDataAction, onNextAction, hideButton, onValidationChange, onRegisterSave, compact }: StepProps & { updateDataAction: (data: Partial<ListingData> & { gradeLabel?: string }) => void }) {
  const tCond = useTranslations("conditions");
  const tLC = useTranslations("listingCreation");
  const conditions = CONDITION_KEYS.map((key, i) => ({
    value: CONDITION_VALUES[i],
    label: tLC(`conditions.${key}.label`),
    description: tLC(`conditions.${key}.desc`),
  }))
  const gradeDescriptions = Object.fromEntries(
    GRADE_KEYS.map((k) => [Number(k), { label: tLC(`grades.${k}.label`), description: tLC(`grades.${k}.desc`) }])
  ) as Record<number, { label: string; description: string }>;
  const [condition, setCondition] = useState(data?.condition || "")
  const [grade, setGrade] = useState<number>(() => {
    if (typeof data?.grade === 'number') return data.grade
    if (typeof data?.grade === 'string') return parseInt(data.grade)
    return 7
  })
  
  // Sync local state with data prop when it changes
  useEffect(() => {
    setCondition(data?.condition || "")
    if (typeof data?.grade === 'number') {
      setGrade(data.grade)
    } else if (typeof data?.grade === 'string') {
      setGrade(parseInt(data.grade))
    }
  }, [data?.condition, data?.grade])
  const [isFormValid, setIsFormValid] = useState(false)
  const gradeSectionRef = useRef<HTMLDivElement>(null)

  // Validate form whenever condition changes
  useEffect(() => {
    if (condition === "new") {
      setIsFormValid(true)
    } else if ((condition === "pre-owned" || condition === "custom") && grade) {
      setIsFormValid(true)
    } else {
      setIsFormValid(false)
    }
  }, [condition, grade])

  // Notify parent about validation status
  useEffect(() => {
    onValidationChange?.(isFormValid)
  }, [isFormValid, onValidationChange])

  const saveData = useCallback(() => {
    if (isFormValid) {
      updateDataAction(({
        condition,
        grade: condition === "new" ? undefined : grade.toString(),
        gradeLabel: condition === "new" ? undefined : gradeDescriptions[grade]?.label,
      } as Partial<ListingData> & { gradeLabel?: string }))
    }
  }, [isFormValid, condition, grade, updateDataAction])

  // Register save function
  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => {
      onRegisterSave?.(null)
    }
  }, [saveData, onRegisterSave])

  const handleNext = () => {
    saveData()
    onNextAction()
  }

  const handleConditionChange = (value: string) => {
    setCondition(value)
    // Reset grade to 5 when switching to pre-owned or custom
    if (value === "pre-owned" || value === "custom") {
      setGrade(5)
      // Scroll to grade section after animation completes
      setTimeout(() => {
        if (gradeSectionRef.current) {
          gradeSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          })
        }
      }, 200) // Wait for animation to complete
    }
    
    // Save data immediately
    const newGrade = (value === "pre-owned" || value === "custom") ? 5 : undefined
    updateDataAction({
      condition: value,
      grade: value === "new" ? undefined : newGrade?.toString(),
      gradeLabel:
        value === "new" || newGrade === undefined ? undefined : gradeDescriptions[newGrade]?.label,
    } as Partial<ListingData> & { gradeLabel?: string })
  }
  
  const handleGradeChange = (value: number) => {
    setGrade(value)
    
    // Save data immediately
    updateDataAction({
      condition,
      grade: value.toString(),
      gradeLabel: gradeDescriptions[value as unknown as number]?.label,
    } as Partial<ListingData> & { gradeLabel?: string })
  }

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.07,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  const needsGrading = condition === "pre-owned" || condition === "custom"

  const containerSpacing = compact ? "space-y-4 px-1 max-w-md mx-auto" : "space-y-6 px-2 max-w-md mx-auto"
  const cardPadding = compact ? "p-3" : "p-4"
  const cardRadius = "rounded-3xl"
  const buttonClasses = compact ? "w-full h-14 text-sm shadow-md rounded-full group" : "w-full h-16 text-base shadow-lg rounded-full group"
  const cardBase = creationFlowBlockClass
  const conditionOption = (selected: boolean) =>
    `flex items-start space-x-3 p-3 rounded-xl cursor-pointer transition-colors ${selected ? "bg-muted" : "bg-muted/50 hover:bg-muted/70"}`
  const labelText = "font-medium text-foreground"
  const descText = "text-sm text-muted-foreground"
  const gradeCardBase = creationFlowBlockClass
  const sectionLabel = creationFlowSectionLabelClass
  const gradeValueClass = "text-3xl font-bold text-foreground"
  const gradeDescClass = "font-medium text-primary mt-1"
  const gradeMutedClass = "text-sm text-muted-foreground mt-2"

  return (
    <div className={containerSpacing} style={{ touchAction: "pan-y" }}>
      <motion.div className="space-y-6" variants={containerVariants} initial="hidden" animate="visible">
        <motion.div className={cn(cardRadius, cardBase, cardPadding)} variants={itemVariants}>
          <p className={cn(sectionLabel, "mb-3")}>{tLC("condition")}</p>
          <div>
            <RadioGroup value={condition} onValueChange={handleConditionChange} className="space-y-2">
              {conditions.map((item) => (
                <motion.div key={item.value} variants={itemVariants}>
                  <Label
                    htmlFor={`condition-${item.value}`}
                    className={conditionOption(condition === item.value)}
                  >
                    <RadioGroupItem value={item.value} id={`condition-${item.value}`} className="mt-1" />
                    <div className="space-y-1">
                      <span className={labelText}>{item.label}</span>
                      <p className={descText}>{item.description}</p>
                    </div>
                  </Label>
                </motion.div>
              ))}
            </RadioGroup>
          </div>
        </motion.div>

        {needsGrading && (
          <motion.div
            ref={gradeSectionRef}
            className={cn(cardRadius, gradeCardBase, cardPadding)}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className={cn(sectionLabel, "mb-3")}>{tLC("conditionGrade")}</p>
            <div className="space-y-6">
              <div className="px-1">
                <Slider
                  tone="panel"
                  value={[grade]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleGradeChange(value[0])}
                  className="py-3"
                />
              </div>

              <div className="text-center">
                <div className={gradeValueClass}>{grade}/10</div>
                <div className={gradeDescClass}>
                  {gradeDescriptions[grade]?.label}
                </div>
                <p className={gradeMutedClass}>
                  {gradeDescriptions[grade]?.description}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {!hideButton && (
        <div className="pt-6">
          <Button
            onClick={handleNext}
            disabled={!isFormValid}
            className={buttonClasses}
            size={compact ? "default" : "lg"}
          >
            Continue
            <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      )}

      {/* Spacer to prevent content from being hidden behind the fixed button */}
    </div>
  )
}

